import { apiClient } from './api';
import { isOperationsPath } from './operationsScope';

export type AssistantTool = { name: string; description: string; write: boolean };
export type AssistantConfiguration = {
  enabled: boolean;
  ready: boolean;
  reason?: string;
  company?: { id: string; name: string };
  environment?: string;
  tools: AssistantTool[];
  limits: {
    maxMessageChars?: number;
    maxTurns?: number;
    maxSteps?: number;
    [key: string]: unknown;
  };
};
export type AssistantUsage = {
  inputTokens?: number;
  outputTokens?: number;
  cachedTokens?: number;
  reservationTokens?: number;
  calls?: number;
  model?: string;
  input_tokens?: number;
  output_tokens?: number;
  cached_input_tokens?: number;
  total_tokens?: number;
  unknown?: boolean;
  [key: string]: unknown;
};
export type AssistantCard = {
  id: string;
  type: string;
  title: string;
  data: unknown;
  action_id?: string;
  href?: string;
};
export type AssistantMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  cards?: AssistantCard[];
  usage?: AssistantUsage;
};
export type AssistantSession = {
  id: string;
  title: string;
  revision: number;
  updated_at?: string;
  created_at?: string;
  claim_id?: string | null;
  lease_until?: string | null;
  messages?: AssistantMessage[];
};
export type AssistantAction = {
  id: string;
  request_id?: string;
  fingerprint?: string;
  tool: string;
  status: string;
  company_id?: string;
  conversation_id?: string | null;
  preview: Record<string, unknown>;
  result?: unknown;
  error?: string | null;
  created_at?: string;
  expires_at?: string;
  approved_at?: string | null;
  delegation_id?: string | null;
};
export type AssistantDetail = {
  session: AssistantSession;
  messages: AssistantMessage[];
  actions: AssistantAction[];
};
export type AssistantDelegationInput = {
  name: string;
  action_ids: string[];
  expires_at: string;
  max_runs: number;
  max_amount_cents: number;
  source_keys?: string[];
  recipient_emails?: string[];
};
export type AssistantDelegation = {
  id: string;
  name: string;
  expires_at: string;
  tools?: string[];
  action_ids?: string[];
  max_runs?: number;
  max_amount_cents?: number;
  source_keys?: string[];
  recipient_emails?: string[];
  constraints?: {
    tools: string[];
    argument_hashes: string[];
    record_ids?: string[];
    source_names?: string[];
    recipients?: string[];
    max_actions_per_run?: number;
    max_amount_minor_per_run?: number;
    budget_amount_minor?: number;
    max_runs: number;
  };
  status?: string;
  paused_at?: string | null;
  revoked_at?: string | null;
  used_runs?: number;
  reserved_amount_minor?: number;
  run_count?: number;
  version?: number;
  owner_id?: string;
  created_at?: string;
};
export type AssistantTurnInput = {
  message_id: string;
  message: string;
  expected_revision: number;
  delegation_id?: string;
};
export type AssistantIntakeInput = {
  source: string;
  csv: string;
  request_id: string;
  delegation_id?: string;
};

const base = '/operator-assistant';
const idPath = (id: string) => encodeURIComponent(id);
export const operatorAssistantAPI = {
  configuration: (signal?: AbortSignal) =>
    apiClient.get<AssistantConfiguration>(`${base}/config`, { signal }).then((r) => r.data),
  sessions: (signal?: AbortSignal) =>
    apiClient.get<AssistantSession[]>(`${base}/sessions`, { signal }).then((r) => r.data),
  createSession: (title?: string) =>
    apiClient.post<AssistantSession>(`${base}/sessions`, title ? { title } : {}).then((r) => r.data),
  detail: (id: string, signal?: AbortSignal) =>
    apiClient.get<AssistantDetail>(`${base}/sessions/${idPath(id)}`, { signal }).then((r) => ({ ...r.data, actions: (r.data.actions || []).map(assistantActionSummary) })),
  turn: (id: string, input: AssistantTurnInput) =>
    apiClient.post<AssistantDetail>(`${base}/sessions/${idPath(id)}/turns`, input, {
      timeout: 120000,
    }).then((r) => ({ ...r.data, actions: (r.data.actions || []).map(assistantActionSummary) })),
  actions: (signal?: AbortSignal) =>
    apiClient.get<AssistantAction[]>(`${base}/actions`, { signal }).then((r) => r.data.map(assistantActionSummary)),
  action: (id: string, signal?: AbortSignal) =>
    apiClient.get<AssistantAction>(`${base}/actions/${idPath(id)}`, { signal }).then((r) => r.data),
  decide: (id: string, approve: boolean) =>
    apiClient.post<AssistantAction>(`${base}/actions/${idPath(id)}/${approve ? 'approve' : 'cancel'}`, {}).then((r) => r.data),
  delegate: (id: string, delegationId: string) =>
    apiClient.post<AssistantAction>(`${base}/actions/${idPath(id)}/delegate`, { delegation_id: delegationId }).then((r) => r.data),
  delegations: (signal?: AbortSignal) =>
    apiClient.get<AssistantDelegation[]>(`${base}/delegations`, { signal }).then((r) => r.data),
  createDelegation: (input: AssistantDelegationInput) =>
    apiClient.post<AssistantDelegation>(`${base}/delegations`, input).then((r) => r.data),
  pauseDelegation: (id: string) =>
    apiClient.post<AssistantDelegation>(`${base}/delegations/${idPath(id)}/pause`, {}).then((r) => r.data),
  intake: (input: AssistantIntakeInput) =>
    apiClient.post<{ action: AssistantAction; review_url?: string }>(`${base}/intake`, input, { timeout: 120000 }).then((r) => r.data),
};

export function assistantActionSummary(action: AssistantAction): AssistantAction {
  return {
    id: action.id, request_id: action.request_id, tool: action.tool, status: action.status,
    company_id: action.company_id, conversation_id: action.conversation_id, fingerprint: action.fingerprint,
    preview: { operation: action.tool, tenant: action.company_id, summary: true },
    error: action.error, created_at: action.created_at, expires_at: action.expires_at,
    approved_at: action.approved_at, delegation_id: action.delegation_id,
  };
}

export function assistantRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function validateMappedCsv(csv: string): string | null {
  if (!csv.trim()) return 'The CSV file is empty.';
  if (csv.length > 1800000) return 'The CSV exceeds the 1,800,000-character limit.';
  const header = csv.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0];
  const columns = header.split(',').map((column) => column.trim().replace(/^"([a-z_]+)"$/, '$1'));
  const allowed = ['key', 'operation', 'data_json', 'existing_id', 'account_key', 'customer_key', 'reservation_key'];
  if (!['key', 'operation', 'data_json'].every((column) => columns.includes(column)) ||
      new Set(columns).size !== columns.length || columns.some((column) => !allowed.includes(column))) {
    return 'CSV columns must include key, operation and data_json; optional columns are existing_id, account_key, customer_key and reservation_key.';
  }
  if (csv.includes('\u0000')) return 'The CSV contains invalid text.';
  return null;
}

export function assistantError(error: unknown, fallback: string): string {
  const value = error as { response?: { data?: { message?: unknown }; status?: number } };
  const message = value?.response?.data?.message;
  if (typeof message === 'string') return message.slice(0, 1200);
  if (Array.isArray(message) && message.every((item) => typeof item === 'string')) {
    return message.join('; ').slice(0, 1200);
  }
  if (value?.response?.status === 403) return 'Your current account cannot access this operation.';
  if (value?.response?.status === 404) return 'MODAL Assistant is not available for this account.';
  if (value?.response?.status === 429) return 'The assistant limit has been reached. Existing records and receipts remain available.';
  return fallback;
}

export function delegationActive(rule: AssistantDelegation, now = Date.now()): boolean {
  const maximum = rule.constraints?.max_runs ?? rule.max_runs;
  return !rule.paused_at && !rule.revoked_at &&
    (!rule.status || rule.status === 'active') &&
    new Date(rule.expires_at).getTime() > now &&
    typeof maximum === 'number' && (rule.used_runs ?? rule.run_count ?? 0) < maximum;
}

export function assistantStatus(status: string): string {
  const labels: Record<string, string> = {
    pending_review: 'Awaiting review', approved: 'Approved - queued', running: 'Running',
    succeeded: 'Completed', needs_review: 'Needs reconciliation', cancelled: 'Cancelled', expired: 'Expired',
  };
  return labels[status] || status.replace(/_/g, ' ');
}

export function assistantLink(href?: string): string | undefined {
  if (!href || href.startsWith('//') || href.includes('\\') || Array.from(href).some((character) => character.charCodeAt(0) <= 32)) return undefined;
  try {
    const origin = window.location.origin;
    const url = new URL(href, origin);
    if (url.username || url.password) return undefined;
    if (url.origin === origin && /^\/admin(?:\/|$)/.test(url.pathname)) {
      if (isOperationsPath()) {
        return url.pathname === '/admin/vehicles' ? `/operations${url.pathname}${url.search}${url.hash}` : undefined;
      }
      return `${url.pathname}${url.search}${url.hash}`;
    }
    // Only established customer-facing MODAL destinations may leave this admin route.
    if (url.protocol === 'https:' &&
        (url.hostname === 'modalhub.net' || url.hostname.endsWith('.modalhub.net')) &&
        /^\/(?:quote|reservation|pay\/(?:request|invoice)|customer)(?:\/|$)/.test(url.pathname)) {
      return url.href;
    }
  } catch { /* Invalid destinations are not rendered as links. */ }
  return undefined;
}
